import { Channels as Channel } from "../generated/django-enums";
import { getGlobalHeadersAsObject } from "../global-headers";
import type { LlmDebugInfo } from "../llm/llm-debug";
import type { DRFListResponseWrapper } from "../types";

export { Channel };

export type DefaultTemplate = {
	id: number;
	template: number;
	channel: Channel;
	assignment: number;
};

export type TemplateContent = {
	id: number;
	created: string;
	updated: string;
	template: string;
	subject: string;
	language_code: string;
	country_code?: string;
	language_name?: string;
	conversation_template: number;
	creator: number;
};

export type ConversationTemplate = {
	id: number;
	created: string;
	updated: string;
	next_email: number;
	use_email: boolean;
	next_inmail: number;
	use_inmail: boolean;
	next_linkedin_message: number;
	use_linkedin_message: boolean;
	next_free_inmail: number;
	use_free_inmail: boolean;
	next_group_message: number;
	use_group_message: boolean;
	next_sms: number;
	use_sms: boolean;
	use_all_methods: boolean;
	next_pitch_delay: string;
	name: string;
	priority: number;
	use_manually: boolean;
	assignment: number | null;
};

export const nextPointers = [
	"next_email",
	"next_inmail",
	"next_linkedin_message",
	"next_free_inmail",
	"next_group_message",
	"next_sms",
];

export const templateTranslator = {
	next_email: "<div>If template was sent<br> as email</div>",
	next_inmail: "<div>If template was sent<br> as inmail</div>",
	next_linkedin_message: "<div>If template was sent<br> as linkedin message</div>",
	next_free_inmail: "<div>If template was sent<br> as free inmail</div>",
	next_group_message: "<div>If template was sent<br> as group message</div>",
	next_sms: "<div>If template was sent<br> as sms</div>",
	EMAIL: "Email",
	MESSAGE: "Message",
	RECRUITER_FREE_INMAIL: "Free Inmail",
	PAID_INMAIL: "Paid Inmail",
	GROUP_MESSAGE: "Group message",
	SMS: "Sms",
};

export const getConversationTemplates = (): Promise<ConversationTemplate[]> => {
	return fetch("/api/v2/conversation_templates/", {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export const getDefaultTemplateByAssignmentId = ({
	assignmentId,
}: {
	assignmentId: number;
}): Promise<DRFListResponseWrapper<DefaultTemplate>> => {
	if (!assignmentId || typeof assignmentId !== "number") {
		throw new Error(
			`getDefaultTemplateByAssignmentId called with invalid argument id '${assignmentId}'. Expected number`
		);
	}

	return fetch(`/api/v2/defaults/?assignment_id=${assignmentId}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const addConversationTemplate = (
	name: string,
	content: string,
	subject?: string
): Promise<Omit<ConversationTemplate, "id" | "created" | "updated">> => {
	return fetch(`/api/v2/conversation_templates/`, {
		method: "POST",
		body: JSON.stringify({
			name,
			subject,
			template: content,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export const createConversationTemplate = async ({
	conversationTemplate,
}: {
	conversationTemplate: ConversationTemplate & { _existing?: boolean };
}): Promise<ConversationTemplate> => {
	// If we receive a template marked as existing or with an ID, just return it
	if (conversationTemplate._existing || conversationTemplate.id > 0) {
		// Remove the _existing flag before returning
		const { _existing, ...template } = conversationTemplate;
		return template;
	}

	const response = await fetch(`/api/v2/conversation_templates/`, {
		method: "POST",
		// Remove the _existing flag if it exists
		body: JSON.stringify(
			conversationTemplate._existing
				? { ...conversationTemplate, _existing: undefined }
				: conversationTemplate
		),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	});

	const data = await response.json();

	if (!response.ok) {
		// Extract validation error message from DRF response
		let errorMessage = "Failed to create conversation template";
		if (data && typeof data === "object") {
			// DRF returns errors like {"name": ["error message"]} or {"non_field_errors": ["..."]}
			const firstKey = Object.keys(data)[0];
			if (firstKey && Array.isArray(data[firstKey])) {
				errorMessage = data[firstKey][0];
			} else if (typeof data[firstKey] === "string") {
				errorMessage = data[firstKey];
			}
		}
		throw new Error(errorMessage);
	}

	return data;
};

export const updateConversationTemplate = async (
	id: number,
	formData: Partial<ConversationTemplate>
): Promise<ConversationTemplate> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`updateConversationTemplate called with invalid argument id '${id}'. Expected number`
		);
	}
	const response = await fetch(`/api/v2/conversation_templates/${id}/`, {
		method: "PATCH",
		body: JSON.stringify(formData),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	});

	const data = await response.json();

	if (!response.ok) {
		// Extract validation error message from DRF response
		let errorMessage = "Failed to update conversation template";
		if (data && typeof data === "object") {
			const firstKey = Object.keys(data)[0];
			if (firstKey && Array.isArray(data[firstKey])) {
				errorMessage = data[firstKey][0];
			} else if (typeof data[firstKey] === "string") {
				errorMessage = data[firstKey];
			}
		}
		throw new Error(errorMessage);
	}

	return data;
};

export const deleteConversationTemplate = (id: number): Promise<any> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`deleteConversationTemplate called with invalid argument id '${id}'. Expected number`
		);
	}
	return fetch(`/api/v2/conversation_templates/${id}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => console.log(res))
		.catch((err) => console.log(err));
};

export const getConversationTemplateDefaults = (
	id: number
): Promise<DefaultTemplate[]> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getConversationTemplateDefaults called with invalid argument id '${id}'. Expected number`
		);
	}

	return fetch(`/api/v2/conversation_templates/${id}/defaults/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export const getDefaults = (): Promise<DRFListResponseWrapper<DefaultTemplate[]>> => {
	return fetch(`/api/v2/defaults/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

// TODO: UPDATE THESE WITH NEW ENDPOINT
export const addConversationTemplateDefault = (
	template: number,
	channel: number,
	assignment?: number
): Promise<DefaultTemplate> => {
	if (!template || typeof template !== "number") {
		throw new Error(
			`getConversationTemplateDefaults called with invalid argument id '${template}'. Expected number`
		);
	}

	return fetch(`/api/v2/defaults/`, {
		method: "POST",
		body: JSON.stringify({
			channel,
			assignment,
			template,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export const deleteConversationTemplateDefault = (
	id: number,
	template: number
): Promise<void> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`deleteConversationTemplateDefault called with invalid argument id '${id}'. Expected number`
		);
	}
	if (!template || typeof template !== "number") {
		throw new Error(
			`deleteConversationTemplateDefault called with invalid argument template '${template}'. Expected number`
		);
	}

	return fetch(`/api/v2/conversation_templates/${template}/defaults/${id}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => console.log(res))
		.catch((err) => console.log(err));
};

export const deleteDefault = (defaultId: number): Promise<void> => {
	return fetch(`/api/v2/defaults/${defaultId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (res.status !== 204) {
			throw new Error("Could not delete default");
		}
		console.log(res);
	});
};

export const getTemplateContents = (
	conversationTemplateId?: number
): Promise<TemplateContent[]> => {
	const url = conversationTemplateId
		? `/api/v2/template_content/?conversation_template=${conversationTemplateId}`
		: "/api/v2/template_content/";

	return fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export const createTemplateContent = async (
	data: Omit<TemplateContent, "id" | "created" | "updated" | "creator">
): Promise<TemplateContent> => {
	const response = await fetch("/api/v2/template_content/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		throw new Error("Failed to create template content");
	}

	return response.json();
};

export const updateTemplateContent = (
	id: number,
	data: Partial<TemplateContent>
): Promise<TemplateContent> => {
	return fetch(`/api/v2/template_content/${id}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	})
		.then((res) => res.json())
		.catch((err) => console.log(err));
};

export type TemplateContentForPerson = {
	template: string;
	rendered_template: string;
	subject: string;
	language_code: string;
	country_code: string | null;
	language_name: string;
	template_id: number;
	template_name: string;
};

export const getTemplateContentForPerson = (
	assignmentId: number,
	personId: number
): Promise<TemplateContentForPerson> => {
	return fetch(
		`/api/v2/assignment/${assignmentId}/template_content/?person_id=${personId}`,
		{
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		}
	).then((res) => {
		if (!res.ok) {
			return null;
		}
		return res.json();
	});
};

export type TemplateUsageInfo = {
	id: number;
	name: string;
	email_count: number;
	linkedin_count: number;
	sms_count: number;
	total_usage: number;
	is_default: boolean;
	is_referenced: boolean;
	can_delete: boolean;
};

export const getTemplateUsage = (
	assignmentId: number
): Promise<TemplateUsageInfo[]> => {
	return fetch(
		`/api/v2/conversation_templates/usage/?assignment_id=${assignmentId}`,
		{
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		}
	).then((res) => {
		if (!res.ok) {
			throw new Error("Failed to fetch template usage");
		}
		return res.json();
	});
};

export type GenerateTemplatesResponse = {
	template_ids: number[];
	main_template_id: number;
};

export const generateConversationTemplates = async (
	assignmentId: number
): Promise<GenerateTemplatesResponse> => {
	const response = await fetch(`/api/v2/conversation_templates/generate/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ assignment_id: assignmentId }),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || "Failed to generate templates");
	}

	return response.json();
};

export type TemplatePreviewContent = {
	subject: string;
	body: string;
};

export type TemplatePreview = {
	name: string;
} & Record<`content_${string}`, TemplatePreviewContent>;

export type GenerateTemplatePreviewResponse = {
	templates: TemplatePreview[];
	languages: string[];
	language_labels: Record<string, string>;
	/** Present only when `debug` was requested by a staff user. */
	debug?: LlmDebugInfo | null;
};

export const generateTemplatePreview = async (params: {
	role_title: string;
	company_name: string;
	role_description: string;
	compensation_range?: string;
	target_country?: string;
	target_languages?: string[];
	hook?: string;
	scope?: string;
	proof_points?: string[];
	user_feedback?: string;
	/** Staff-only: ask for the rendered prompt and raw response back. */
	debug?: boolean;
}): Promise<GenerateTemplatePreviewResponse> => {
	const response = await fetch(`/api/v2/conversation_templates/generate_preview/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || "Failed to generate template preview");
	}

	return response.json();
};

export const createTemplatesFromPreview = async (params: {
	assignment_id: number;
	templates: TemplatePreview[];
}): Promise<GenerateTemplatesResponse> => {
	const response = await fetch(
		`/api/v2/conversation_templates/create_from_preview/`,
		{
			method: "POST",
			headers: {
				...getGlobalHeadersAsObject(),
				"Content-Type": "application/json",
			},
			body: JSON.stringify(params),
		}
	);

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || "Failed to create templates from preview");
	}

	return response.json();
};

export type InviteNoteData = {
	enabled: boolean;
	notes: Record<string, string>;
	languages: string[];
};

export const getInviteNote = async (assignmentId: number): Promise<InviteNoteData> => {
	const response = await fetch(
		`/api/v2/conversation_templates/invite_note/?assignment_id=${assignmentId}`,
		{ headers: getGlobalHeadersAsObject() }
	);
	if (!response.ok) {
		throw new Error("Failed to load invitation note");
	}
	const data = await response.json();
	return {
		enabled: Boolean(data.enabled),
		notes: data.notes ?? {},
		languages: data.languages?.length ? data.languages : ["en"],
	};
};

export const saveInviteNote = async (
	assignmentId: number,
	enabled: boolean,
	notes: Record<string, string>
): Promise<void> => {
	const response = await fetch(`/api/v2/conversation_templates/save_invite_note/`, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ assignment_id: assignmentId, enabled, notes }),
	});
	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || "Failed to save invitation note");
	}
};

export const generateInviteNote = async (
	assignmentId: number
): Promise<Record<string, string>> => {
	const response = await fetch(
		`/api/v2/conversation_templates/generate_invite_note/`,
		{
			method: "POST",
			headers: {
				...getGlobalHeadersAsObject(),
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ assignment_id: assignmentId }),
		}
	);
	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || "Failed to generate invitation note");
	}
	const data = await response.json();
	return data.notes ?? {};
};
